// Members-only access while FnO Bazar is in testing.
// Mirrors the checks in database.rules.json (see scripts/build-database-rules.js);
// the rules are what actually protect the data, this only drives the UI.
import { ref, get, set, remove, serverTimestamp } from "firebase/database";
import { futureDB } from "../firebase";
import fixedAdmins from "./fixedAdmins.json";

// Shared with scripts/build-database-rules.js so the UI and the rules agree.
export const FIXED_ADMIN_EMAILS = fixedAdmins.FIXED_ADMIN_EMAILS;

export const emailKey = (email) => email.trim().toLowerCase().replace(/\./g, ",");
export const keyToValue = (key, type) => (type === "email" ? key.replace(/,/g, ".") : key);

export const isFixedAdminEmail = (email) => Boolean(email) && FIXED_ADMIN_EMAILS.includes(email.trim().toLowerCase());
export const isFixedAdmin = (user) => Boolean(user && user.emailVerified && isFixedAdminEmail(user.email));

// The allowlist key a signed-in user would be listed under, if any.
const memberKeys = (user) => {
  const keys = [];
  if (user.email) keys.push(emailKey(user.email));
  if (user.phoneNumber) keys.push(user.phoneNumber);
  return keys;
};

export class AccessDeniedError extends Error {
  constructor() {
    super("This account isn't on the members list yet. Ask an FnO Bazar admin to add you, then log in again.");
    this.name = "AccessDeniedError";
  }
}

// Resolves { isAdmin, isFixedAdmin } for a signed-in user, or throws AccessDeniedError.
export async function resolveAccess(user) {
  if (isFixedAdmin(user)) {
    // Keep the admins node in step so other tools can see who the fixed admins are.
    await set(ref(futureDB, `admins/${user.uid}`), true).catch(() => {});
    return { isAdmin: true, isFixedAdmin: true };
  }

  const adminSnap = await get(ref(futureDB, `admins/${user.uid}`)).catch(() => null);
  const isAdmin = Boolean(adminSnap && adminSnap.val() === true);

  for (const key of memberKeys(user)) {
    const snap = await get(ref(futureDB, `allowlist/${key}`)).catch(() => null);
    if (snap && snap.exists()) {
      if (snap.val().uid !== user.uid) {
        await set(ref(futureDB, `allowlist/${key}/uid`), user.uid).catch(() => {});
      }
      return { isAdmin, isFixedAdmin: false };
    }
  }
  if (isAdmin) return { isAdmin, isFixedAdmin: false };
  throw new AccessDeniedError();
}

// ---------- Admin panel operations ----------

export const normaliseMember = (raw) => {
  const value = raw.trim();
  if (/^\S+@\S+\.\S+$/.test(value)) return { type: "email", value: value.toLowerCase(), key: emailKey(value) };
  const digits = value.replace(/[\s()-]/g, "");
  if (/^\+?\d{10,15}$/.test(digits)) {
    // Indian numbers without a country code are the common case.
    const phone = digits.startsWith("+") ? digits : digits.length === 10 ? `+91${digits}` : `+${digits}`;
    return { type: "phone", value: phone, key: phone };
  }
  return null;
};

export async function listMembers() {
  const [allowSnap, adminSnap] = await Promise.all([
    get(ref(futureDB, "allowlist")),
    get(ref(futureDB, "admins")),
  ]);
  const admins = adminSnap.val() || {};
  return Object.entries(allowSnap.val() || {})
    .map(([key, entry]) => ({
      key,
      ...entry,
      isAdmin: Boolean(entry.uid && admins[entry.uid] === true),
      isFixedAdmin: entry.type === "email" && isFixedAdminEmail(entry.value),
    }))
    .sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
}

export async function addMember(raw, addedBy) {
  const member = normaliseMember(raw);
  if (!member) throw new Error("Enter an email address or a 10-digit mobile number.");
  const existing = await get(ref(futureDB, `allowlist/${member.key}`));
  if (existing.exists()) throw new Error(`${member.value} is already a member.`);
  await set(ref(futureDB, `allowlist/${member.key}`), {
    type: member.type,
    value: member.value,
    addedBy: addedBy || null,
    addedAt: serverTimestamp(),
  });
  return member;
}

export async function removeMember(member, actorIsFixedAdmin) {
  if (member.isFixedAdmin) throw new Error("Fixed admins can't be removed.");
  if (member.isAdmin && !actorIsFixedAdmin) throw new Error("Only a fixed admin can remove another admin.");
  if (member.isAdmin) await set(ref(futureDB, `admins/${member.uid}`), false);
  await remove(ref(futureDB, `allowlist/${member.key}`));
}

export async function setAdmin(member, value) {
  if (member.isFixedAdmin) throw new Error("Fixed admins can't be changed.");
  if (!member.uid) throw new Error(`${member.value} needs to log in once before they can be made an admin.`);
  await set(ref(futureDB, `admins/${member.uid}`), value);
}

